import React, {
  FC,
  Ref,
  RefObject,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';

import AddModule from './components/AddModule';
import agentProfileResource from 'src/actions/agent/agentProfileResource';
import AnonymousModuleModal, { AnonymousModuleModalRef } from './components/AnonymousModule/AnonymousModuleModal';
import { Body } from 'src/presentation/components/forms/Layout';
import ChildrenNodes from './components/ChildrenNodes/ChildrenNodes';
import ControlBar from './components/ControlBar.tsx/ControlBar';
import DeleteConfirmationModal, {
  DeleteConfirmationModalRef,
} from 'src/presentation/components/pages/sidekick/threads/components/ThreadBuilder/DeleteConfirmationModal';
import { FormEditorProps } from 'src/presentation/components/forms/Form';
import { getThankfulBaseUrl } from './utils/utils';
import { H2 } from '@ad-scripts/presentation/components/common/headers';
import { ISidekickThreadEditorContext, SidekickThreadEditorContext } from './SidekickThreadEditorContext';
import NewThreadModal, { NewThreadModalRef } from './components/NewThreadModal';
import {
  selectCurrentThread,
  selectCustomerId,
  selectIsDirty,
  selectModuleSpecsMap,
  selectShowLoadingIndicator,
  selectThreadMeta,
  setCurrentThreadId,
  setShowLoadingIndicator,
} from './actions/threadsUiSlice';
import { selectRevisionID } from './actions/revisionActions';
import {
  SIDE_PANEL_WIDTH,
  THREAD_CANVAS_BOTTOM_MARGIN,
  THREAD_CANVAS_WIDTH,
  THREAD_FLOW_TOP_MARGIN,
} from './SidekickConstants';
import sidekickAdminsResource from 'src/actions/sidekick/sidekickAdminsResource';
import sidekickCustomerResource from 'src/actions/sidekick/sidekickCustomerResource';
import sidekickExternalActionsResource from 'src/actions/sidekick/sidekickExternalActionsResource';
import sidekickIntegrationsResource from 'src/actions/sidekick/sidekickIntegrationsResource';
import sidekickModuleSpecsResource from 'src/actions/sidekick/sidekickModuleSpecsResource';
import sidekickRevisionsResource from 'src/actions/sidekick/sidekickRevisionsResource';
import { SidePanelRef } from './components/SidePanel/SidePanel';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import ThreadBuilder from './components/ThreadBuilder';
import ThreadParentsPanel from './components/ParentsPanel/ThreadParentsPanel';
import useResource, {
  STATUS_FAILED,
  STATUS_LOADING,
  getConsolidatedState,
  isLoadingStatus,
  someFailed,
  useResourceValues,
} from 'src/presentation/hooks/useResource';
import { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import useRouter from 'src/presentation/hooks/useRouter';
import useWindowResize from './hooks/useWindowResize';
import useTopics from 'src/presentation/hooks/useTopics';
import sidekickBTreeResourcesResource from 'src/actions/sidekick/sidekickBTreeResourcesResource';
import ProgressBar from '@ad-scripts/presentation/components/common/progress_bar';
import { toastError } from 'src/actions/toast';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import EditorLayout from './components/AnonymousModule/EditorLayout';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import topicsResource from 'src/actions/topics/topicsResource';
import topicsHierarchyResource from 'src/actions/topics/topicsHierarchyResource';

const dataAidPrefix = 'admin-sidekick-thread';

const SidekickThreadEditor: FC<FormEditorProps> = ({ item, root }: FormEditorProps): React.ReactElement => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const revision = useSelector(selectRevisionID);

  const { loadingState: loadingStateModuleSpecs } = useResource(sidekickModuleSpecsResource, {
    fetch: true,
    params: { customerId },
  });
  const { loadingState: loadingStateExternalActions } = useResource(sidekickExternalActionsResource, {
    fetch: true,
    params: { customerId },
  });
  const { loadingState: loadingStateBTreeResources } = useResource(sidekickBTreeResourcesResource, {
    fetch: true,
    params: { customerId },
  });

  const { loadingState: loadingStateIntegrations } = useResource(sidekickIntegrationsResource, {
    fetch: true,
    params: { customerId },
  });
  useResource(sidekickRevisionsResource, {
    fetch: true,
    params: { customerId },
    body: { idDesc: true, limit: 100, flowID: item.id },
    headers: { 'X-Method': 'GET' },
  });

  useTopics();
  const { loadingState: topicsLoadingState } = useResourceValues(topicsResource);
  const { loadingState: topicsHierarchyLoadingState } = useResourceValues(topicsHierarchyResource);

  const currentThread = useSelector(selectCurrentThread);

  const loadingStates = [
    loadingStateExternalActions,
    loadingStateIntegrations,
    loadingStateModuleSpecs,
    topicsHierarchyLoadingState,
    topicsLoadingState,
  ];
  const fetchError = someFailed(...loadingStates);
  let loadingState = fetchError ? STATUS_FAILED : getConsolidatedState(loadingStates);

  // handle it separately because this request is failing for some organization (where no glados)
  if (loadingStateBTreeResources === STATUS_LOADING) {
    loadingState = STATUS_LOADING;
  }

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [fetchError, dispatch]);

  useEffect(() => {
    if (loadingState === STATUS_LOADING) {
      dispatch(setShowLoadingIndicator(true));
    } else {
      dispatch(setShowLoadingIndicator(false));
    }
  }, [dispatch, loadingState]);

  useEffect(() => {
    if (loadingState === STATUS_LOADING) {
      return;
    }

    if (item.id !== currentThread?.id) {
      dispatch(setCurrentThreadId(item.id));
    }
  }, [currentThread?.id, dispatch, item.id, loadingState]);

  const threadHasActions = currentThread?.nextData?.length;

  return (
    <StyledBody data-aid={dataAidPrefix} isNew={!threadHasActions}>
      {isLoadingStatus(loadingState) ? (
        <FullScreen>
          <Spinner />
        </FullScreen>
      ) : (
        currentThread?.nextData && <MemoizedEditor root={root} isReadOnly={!!revision} />
      )}
    </StyledBody>
  );
};

const Editor = ({ root, isReadOnly }: { root: string; isReadOnly: boolean }) => {
  const { history } = useRouter();
  const thread = useSelector(selectCurrentThread);
  const isFeatureEnabled = useIsFeatureEnabled();
  const isAdminFlexibleCardEnabled = isFeatureEnabled('adminFlexibleCardEnabled');
  const isDemoMode = isFeatureEnabled('demoMode');

  const threadMeta = useSelector(selectThreadMeta);

  const { value: profile } = useResourceValue(agentProfileResource);
  const { values: admins } = useResourceValues(sidekickAdminsResource);

  const newThreadModalRef = useRef<NewThreadModalRef>(null);

  const deleteConfirmationModalRef = useRef<DeleteConfirmationModalRef>(null);
  const anonymousModuleModalRef = useRef<AnonymousModuleModalRef>(null);

  const { value: customer } = useResourceValue(sidekickCustomerResource);

  const showProgressBar = useSelector(selectShowLoadingIndicator);

  const readOnly = isReadOnly || showProgressBar;

  const sidePanelRef = useRef<SidePanelRef>(null);

  const moduleSpecsMap = useSelector(selectModuleSpecsMap);

  const scrollableRef = useRef<HTMLDivElement>(null);

  const isDirty = useSelector(selectIsDirty);
  const [isConfirmCancelModalVisible, setConfirmCancelModalVisible] = useState(false);
  const handleStay = useCallback(() => {
    setConfirmCancelModalVisible(false);
  }, [setConfirmCancelModalVisible]);
  const onClose = navigateTo => history.push(navigateTo);

  useEffect(() => {
    if (sidePanelRef.current && !thread?.nextData.length) {
      sidePanelRef.current?.close();
      return;
    }
    sidePanelRef.current?.open();
  }, [sidePanelRef, thread]);

  const adminProfile = useMemo(() => admins?.find(admin => admin.email === profile?.email), [profile, admins]);

  const contextValue: ISidekickThreadEditorContext = useMemo(() => {
    const specsMap = cloneDeep(moduleSpecsMap);

    Object.values(specsMap).forEach((spec: any) => {
      if (!spec.modifiers) {
        return;
      }

      spec.modifiers = spec.modifiers?.filter((m: any) => m.name !== 'sendProductRecommendations');
    });

    return {
      anonymousModuleModalRef,
      allowedToAddModules: Object.keys(specsMap).filter(moduleName => {
        if (!moduleName) {
          throw new Error('null spec');
        }
        if (moduleName === 'updateTicket') {
          return false;
        }

        if (moduleName === 'generativeReply' && !adminProfile?.global && !isDemoMode) {
          return false;
        }
        if (moduleName === 'analyzeMessage') {
          return false;
        }
        if (moduleName === 'sendCard' && !isAdminFlexibleCardEnabled) {
          return false;
        }

        return true;
      }),
      moduleSpecsMap: specsMap,
      newThreadModalRef,
      readOnly: readOnly,
      root,
      scrollableRef,
      thankfulUrl: getThankfulBaseUrl(customer),
      deleteConfirmationModalRef,
    };
  }, [
    adminProfile,
    customer,
    isAdminFlexibleCardEnabled,
    isDemoMode,
    moduleSpecsMap,
    newThreadModalRef,
    readOnly,
    root,
    scrollableRef,
  ]);

  const progressBar = <ProgressBar data-aid="progress-bar" isVisible={showProgressBar} />;

  return (
    <>
      <SidekickThreadEditorContext.Provider value={contextValue}>
        {progressBar}

        <EditorLayout
          id="main-connectors"
          controlBarControl={
            <>
              <ControlBar className="controlBar" root={root} />
            </>
          }
          moduleViewControl={
            <>
              <ModulesContainerHeader>
                <StyledModuleHeader>{threadMeta?.name}</StyledModuleHeader>
              </ModulesContainerHeader>
              {thread.nextData.length ? (
                <ThreadBuilder
                  attr=""
                  disabled={readOnly}
                  key={thread.id}
                  modules={thread.nextData}
                  showAddModuleButton
                />
              ) : (
                <EmptyThread readOnly={readOnly} />
              )}
            </>
          }
          hasModules={thread.nextData.length > 0}
          renderParentsPanel={(params: any) => (
            <>
              <ThreadParentsPanel {...params} threadId={thread.id} />
              <ChildrenNodes
                childrenNodesRef={params.childrenNodesRef}
                fixedContainerRef={params.fixedContainerRef}
                moduleContainerRef={params.moduleContainerRef}
              />
            </>
          )}
          readOnly={readOnly}
        />

        <ConfirmCancelModal
          name="Sidekick"
          handleLeave={onClose}
          handleStay={handleStay}
          isModified={isDirty}
          isVisible={isConfirmCancelModalVisible}
        />

        <NewThreadModal ref={newThreadModalRef} />
        <DeleteConfirmationModal ref={deleteConfirmationModalRef} />
        <AnonymousModuleModal ref={anonymousModuleModalRef} />
      </SidekickThreadEditorContext.Provider>
    </>
  );
};

const EmptyThread = ({ readOnly }: { readOnly: boolean }) => {
  return (
    <EmptyThreadContainer>
      <EmptyThreadDescription>
        Build the Thread by adding Actions and the Sub-Actions within them.
        <br /> Start by selecting the add icon that appears below. You&apos;ll find this
        <br /> option under each Action.
      </EmptyThreadDescription>

      <StyledAddModule
        disabled={readOnly}
        className="add-module"
        aboveModuleId={null}
        moduleSpec={null}
        isVisible={true}
      />
    </EmptyThreadContainer>
  );
};

const MemoizedEditor = memo(Editor);

type FixedContainerProps = {
  children: React.ReactNode;
  hasActions: boolean;
  moduleContainerRef: RefObject<HTMLDivElement>;
  scrollableRef: RefObject<HTMLDivElement>;
};

export type FixedContainerRef = {
  recalculate: () => void;
  elementRef: RefObject<HTMLDivElement>;
};

export const FixedContainer = forwardRef(
  ({ children, hasActions, scrollableRef, moduleContainerRef }: FixedContainerProps, ref: Ref<FixedContainerRef>) => {
    const [height, setHeight] = useState(0);
    const [left, setLeft] = useState(0);

    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      elementRef: containerRef,
      recalculate: computePosition,
    }));

    const computePosition = useCallback(() => {
      if (!scrollableRef?.current || !moduleContainerRef.current) {
        return;
      }

      const modulesRect = moduleContainerRef.current.getClientRects()[0];
      setHeight(modulesRect.height + modulesRect.top + 16 + scrollableRef.current.scrollTop);

      const rect = scrollableRef.current.getClientRects()[0];
      let left = rect.width / 2 - THREAD_CANVAS_WIDTH / 2;
      if (left < 0) {
        left = 0;
      }

      setLeft(left);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollableRef, moduleContainerRef, setHeight, setLeft, hasActions]); // ignore hasActions

    useWindowResize({ callback: computePosition, timeout: 0 });

    return (
      <StyledFixedContainer height={height} left={left} ref={containerRef}>
        {children}
      </StyledFixedContainer>
    );
  }
);

export const StyledFixedContainer = styled.div`
  display: block;
  height: ${p => p.height + THREAD_CANVAS_BOTTOM_MARGIN}px;
  left: ${p => p.left}px;
  min-width: ${THREAD_CANVAS_WIDTH}px;
  position: absolute;
  top: 0;
  width: ${THREAD_CANVAS_WIDTH}px;
`;

export const BuilderContainer = styled.div`
  background-color: ${p => p.theme.colors.gray100};
  height: 100%;
  overflow: auto;
  position: relative;
  width: 100%;
`;

export const InternalContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  min-width: ${THREAD_CANVAS_WIDTH}px;
  position: absolute;
  width: 100%;
`;

const StyledAddModule = styled(AddModule)`
  visibility: visible !important;
  width: 100%;
`;

const EmptyThreadContainer = styled.div`
  padding: 0 24px 24px 24px;
`;

const EmptyThreadDescription = styled.div`
  align-items: center;
  color: ${p => p.theme.colors.gray700};
  display: flex;
  justify-content: center;
  line-height: 21.06px;
  padding-bottom: 24px;
  text-align: center;
`;

const ModulesContainerHeader = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 16px;
`;

const StyledModuleHeader = styled(H2)`
  justify-content: center;
  text-align: center;
  text-wrap: wrap;
  width: 100%;
`;

const StyledBody = styled(Body)`
  display: flex;
  align-items: center;
  padding: 0;
  max-width: 100%;
  width: ${p => (p.isNew ? '100%' : `calc(100% - ${SIDE_PANEL_WIDTH})`)};
  height: calc(100vh - ${THREAD_FLOW_TOP_MARGIN});
  margin-right: auto;
`;

const FullScreen = styled.div`
  align-items: center;
  background-color: ${p => p.theme.colors.white};
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  left: 0;
  overflow-y: auto;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 100;
`;

export default SidekickThreadEditor;
